import { useCallback } from "react";
import { DataCollectionData } from "benchmarking-domain/data-collection";
import { CalculationFormula, CalculationFormulaArgumentType } from "benchmarking-domain/questionnaire";
import { DataPointValue } from "benchmarking-domain/data-point";

export const DataCollectionHelper = {

  getValueId: (valueId: string, valueIdPrefix: string | undefined) => {
    return `${valueIdPrefix ? valueIdPrefix + '!' : ''}${valueId}`;
  },

  tryToGetValue: (value: string, globalVariables: DataCollectionData | undefined, dataCollection: DataCollectionData | undefined, valueIdPrefix: string | undefined) => {
    const regex = new RegExp('^\\$[{](.+)[}]');

    const testResult = regex.exec(value);

    if (testResult) {
      const varId = testResult[1];

      return globalVariables?.[varId] ?? dataCollection?.[DataCollectionHelper.getValueId(varId, valueIdPrefix)];
    }

    return value;
  },

  getRefValue: (refDefinition: CalculationFormulaArgumentType, globalVariables: DataCollectionData | undefined, dataCollection: DataCollectionData | undefined, valueIdPrefix: string | undefined): DataPointValue => {
    return typeof refDefinition === 'string'
      ? DataCollectionHelper.tryToGetValue(refDefinition, globalVariables, dataCollection, valueIdPrefix)
      : typeof refDefinition === 'number'
        ? (refDefinition as number)
        : typeof refDefinition === 'boolean'
          ? (refDefinition as boolean)
          : DataCollectionHelper.calculateExpression(refDefinition, globalVariables, dataCollection, valueIdPrefix);
  },

  calculateExpression: (definition: CalculationFormula, globalVariables: DataCollectionData | undefined, dataCollection: DataCollectionData | undefined, valueIdPrefix: string | undefined) => {
    const firstArg = definition.arguments[0];
    let result: DataPointValue = DataCollectionHelper.getRefValue(firstArg, globalVariables, dataCollection, valueIdPrefix) ?? 0;

    for (let i = 1; i < definition.arguments.length; i++) {
      const nextArgument = definition.arguments[i];
      const nextValue = DataCollectionHelper.getRefValue(nextArgument, globalVariables, dataCollection, valueIdPrefix) ?? 0;

      if (nextValue !== undefined && typeof result !== typeof nextValue) {
        return undefined;
      }

      try {
        if (typeof result === 'number' && typeof nextValue === 'number') {
          let precision = Math.max(
            ((result + '').split('.')[1] || '').length,
            ((nextValue + '').split('.')[1] || '').length
          );
          switch (definition.operation) {
            case 'add':
              result += nextValue;
              break;
            case 'sub':
              result -= nextValue;
              break;
            case 'multi':
              result *= nextValue;
              break;
            case 'div':
              if(nextValue === 0) {
                return undefined;
              } else {
                result /= nextValue;
                precision = precision < 4 ? 4 : precision;
              }

              break;
          }

          result = Number(result.toFixed(precision));
        }

        if (typeof result === 'boolean' && typeof nextValue === 'boolean') {
          switch (definition.operation) {
            case 'and':
              result = result && nextValue;
              if(!result) {
                return result;
              }
              break;
            case 'or':
              result = result || nextValue;
              if(result) {
                return result;
              }
              break;
          }
        }

        if (typeof result === 'string' && typeof nextValue === 'string') {
          switch (definition.operation) {
            case 'startsWith':
              result = result.startsWith(nextValue);
              break;
          }
        }

        switch (definition.operation) {
          case 'equals':
            result = result !== undefined && nextValue !== undefined && result === nextValue;
            break;
        }
      } catch (error) {
        return undefined;
      }
    }

    return result;
  },

}
